<template>
  <div class="judgment-result">
    <JpIcons />

    <!-- 任务不存在（如刷新后快照丢失 / id 无效） -->
    <section v-if="!task" class="result-missing">
      <div class="card missing-card">
        <div class="missing-tip">
          未找到该裁判文书记录，可能已被删除或链接失效。
        </div>
        <button class="btn primary" @click="goBack">
          <svg class="icon sm"><use href="#jp-left" /></svg>返回裁判文书
        </button>
      </div>
    </section>

    <section v-else class="result-page">
      <div class="result-top">
        <div class="result-title">
          <!-- 返回图标与诉讼文书编辑页（DocHeader）统一 -->
          <img
            class="back-btn"
            src="@/assets/icons/i-return.png"
            alt="返回"
            @click="goBack"
          />
          <div class="result-name">{{ task.name }} · {{ task.type }}</div>
          <span class="version-badge">生成结果</span>
        </div>
        <div class="result-actions">
          <button class="btn primary" @click="downloadWord">
            <svg class="icon sm"><use href="#jp-download" /></svg>下载文书
          </button>
        </div>
      </div>

      <div class="result-workspace">
        <!-- 左侧：文书只读展示（docx-preview 渲染，无任何编辑入口），内部滚动 -->
        <div class="workspace-left">
          <div v-if="docLoading" class="view-loading">文书加载中…</div>
          <div ref="docxViewRef" class="docx-view"></div>
        </div>
        <!-- 右侧：AI 问答面板（AskFiles 接口，一问一答聊天） -->
        <AiChatPanel class="workspace-right" :task="task" />
      </div>
    </section>
  </div>
</template>

<script setup>
defineOptions({ name: "JudicativePaperResult" });
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { renderAsync } from "docx-preview";
import { viewingTask, saveJudgmentDoc } from "./store";
import { downloadJudgmentDoc } from "@/api/judicativePaper";
import JpIcons from "./components/JpIcons.vue";
import AiChatPanel from "./components/AiChatPanel.vue";

const route = useRoute();
const router = useRouter();

// 列表行（接口数据）跳转时暂存的快照，结果页以此渲染头部信息；
// id 一致才用，避免串数据（刷新后丢失，回落「未找到记录」兜底页）
const task = computed(() => {
  const snap = viewingTask.value;
  return snap && String(snap.id) === String(route.query.id) ? snap : null;
});

const docxViewRef = ref(null); // docx-preview 渲染容器
const docLoading = ref(false);

function goBack() {
  router.push("/home/JudicativePaper");
}

// ---------- 文书只读展示 ----------
// 进入结果页：调 Download 接口按 id 取文书 Blob，docx-preview 渲染成
// 只读 HTML（纯展示，无任何编辑入口）
async function loadGeneratedDoc(t) {
  if (!t) return;
  const container = docxViewRef.value;
  if (!container) return;
  docLoading.value = true;
  try {
    const blob = await downloadJudgmentDoc(t.docId ?? t.id);
    container.innerHTML = "";
    await renderAsync(await blob.arrayBuffer(), container, null, {
      inWrapper: true,
    });
  } catch (err) {
    // 业务 / 状态码错误 http.js 已 toast；此处兜底网络类静默失败
    console.error("[JudicativePaper] load document error:", err);
    message.error("文书加载失败，请稍后重试");
  } finally {
    docLoading.value = false;
  }
}
onMounted(() => loadGeneratedDoc(task.value));
// 同路由不同任务（query.id 变化）时清空旧内容后加载新文书
watch(
  () => route.query.id,
  async () => {
    await nextTick();
    loadGeneratedDoc(task.value);
  },
);

// 下载文书：直接落盘 Download 接口返回的文件（与任务行下载共用 saveJudgmentDoc）
async function downloadWord() {
  const t = task.value;
  if (!t) return;
  try {
    await saveJudgmentDoc(t);
    message.success("文件下载成功");
  } catch (err) {
    console.error("[JudicativePaper] downloadJudgmentDoc error:", err);
    message.error("文书下载失败，请稍后重试");
  }
}
</script>

<style scoped>
.judgment-result {
  --brand: #1768ff;
  --brand-hover: #0e59db;
  --ink: #17243a;
  --line: #e5ebf3;
  --shadow: 0 8px 26px rgba(31, 53, 88, 0.06);
  --radius: 16px;
  width: 100%;
  margin-top: 24px;
  color: var(--ink);
}

.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}

button {
  cursor: pointer;
}

.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.btn:hover {
  background: #f8fbff;
  border-color: #c9d6e8;
}
.btn.primary {
  background: var(--brand);
  border-color: var(--brand);
  color: #fff;
}
.btn.primary:hover {
  background: var(--brand-hover);
}

/* 记录缺失兜底页 */
.result-missing {
  display: grid;
  place-items: center;
  min-height: 320px;
}
.missing-card {
  padding: 34px 40px;
  display: grid;
  justify-items: center;
  gap: 18px;
}
.missing-tip {
  color: #65758e;
  font-size: 14px;
}

/* ---------- 结果页：整页高度与壳层对齐（64 header + 24 上 + 24 下 = 112） ---------- */
.result-page {
  height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
}
.result-top {
  flex: 0 0 58px;
  background: #fff;
  border: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  border-radius: 12px;
  margin-bottom: 12px;
}
.result-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.back-btn {
  width: 36px;
  height: 36px;
  cursor: pointer;
  flex: none;
}
.result-name {
  font-size: 17px;
  font-weight: 780;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 560px;
}
.version-badge {
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eef4ff;
  color: var(--brand);
  font-size: 11px;
  font-weight: 750;
  display: flex;
  align-items: center;
}
.result-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.result-actions .btn {
  height: 32px;
  min-height: 32px;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 12px;
}

/* 布局：左侧文书只读展示 + 右侧 AI 问答面板 */
.result-workspace {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 12px;
  background: #f6f8fc;
  overflow: hidden;
}
.workspace-left {
  flex: 1;
  min-width: 0;
  position: relative;
  border: 1px solid #e4eaf5;
  border-radius: 14px;
  overflow: hidden;
  background: #f6f8fc;
}
.workspace-right {
  width: 430px;
  flex-shrink: 0;
  border: 1px solid #e4eaf5;
  overflow: hidden;
  border-radius: 14px;
}
@media (max-width: 1380px) {
  .workspace-right {
    width: 390px;
  }
}
/* 文书只读展示：docx-preview 渲染，内部滚动，自带页面卡片观感 */
.docx-view {
  height: 100%;
  overflow: auto;
  padding: 24px 0;
}
/* docx-preview 默认灰底对齐展示区底色；页面间距略放开 */
.docx-view :deep(.docx-wrapper) {
  background: #f6f8fc;
  padding: 0;
}
.docx-view :deep(.docx-wrapper > section.docx) {
  margin-bottom: 18px;
  box-shadow: 0 6px 24px rgba(31, 53, 88, 0.1);
}
/* 加载提示 */
.view-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  color: #65758e;
  font-size: 13px;
  background: rgba(246, 248, 252, 0.75);
}
</style>
